import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import GridOnOutlinedIcon from '@mui/icons-material/GridOnOutlined';
import { DispositionMatrixResponse } from '../../../../services/benchmarkService';
import { NotComputed, PanelError, PanelSkeleton, SectionCard, TierPill } from '../../BenchmarkPrimitives';
import { count } from '../../BenchmarkVocabulary';
import { tierAxis } from './resultsVocabulary';
import { dsVar, ink } from '../../../../theme/tokens';

interface AgreementMatrixProps {
    matrix: DispositionMatrixResponse | null;
    loading?: boolean;
    error?: string;
    onRetry?: () => void;
}

/** The row for cases whose own tier has no approved mapping. */
const NOT_COMPARABLE = '__not_comparable__';

const headSx = {
    px: 1.5, py: 1, fontSize: '0.6875rem', fontWeight: 600, color: dsVar('fg-muted'),
    textTransform: 'uppercase' as const, letterSpacing: '0.04em', whiteSpace: 'nowrap' as const
};

/**
 * Your tier against the platform's, case by case.
 *
 * Plain counts in a grid, not a heat map: at the sizes a supplied sample comes
 * in, shading would make one case look like a pattern. The diagonal is where
 * the two agree; above it the platform ranked higher, below it lower.
 */
const AgreementMatrix: React.FC<AgreementMatrixProps> = ({ matrix, loading, error, onRetry }) => {
    const model = useMemo(() => {
        if (!matrix) return null;
        const cells = new Map<string, number>();
        const yours = new Set<string | null>();
        const platform = new Set<string | null>();
        let total = 0;
        for (const link of matrix.tierMigrationFlow) {
            if (!link.toAgent) continue;
            const row = link.fromIncumbent || NOT_COMPARABLE;
            const key = `${row}|${link.toAgent}`;
            cells.set(key, (cells.get(key) ?? 0) + link.count);
            yours.add(link.fromIncumbent || null);
            platform.add(link.toAgent);
            total += link.count;
        }
        if (total === 0) return null;
        const columns = tierAxis(platform);
        const rows: string[] = tierAxis(yours);
        if (yours.has(null)) rows.push(NOT_COMPARABLE);
        return { cells, columns, rows, total };
    }, [matrix]);

    if (error) {
        return (
            <SectionCard title="Your tiers against the platform's" icon={<GridOnOutlinedIcon fontSize="small" />}>
                <PanelError message={error} onRetry={onRetry} />
            </SectionCard>
        );
    }
    if (loading && !matrix) return <PanelSkeleton height={280} />;

    const cell = (row: string, column: string) => model?.cells.get(`${row}|${column}`) ?? 0;
    const rowTotal = (row: string) => model!.columns.reduce((sum, c) => sum + cell(row, c), 0);
    const columnTotal = (column: string) => model!.rows.reduce((sum, r) => sum + cell(r, column), 0);

    return (
        <SectionCard
            title="Your tiers against the platform's"
            subtitle="Counts of cases. Your tier is shown on the platform's scale. On the outlined diagonal the two agree."
            icon={<GridOnOutlinedIcon fontSize="small" />}
            accent="#A855F7"
        >
            {!model ? (
                <NotComputed detail="This run recorded no scored cases to compare." />
            ) : (
                <Box sx={{ overflowX: 'auto' }}>
                    <Box component="table" sx={{ borderCollapse: 'collapse', minWidth: 480 }} aria-label="Agreement between your tiers and the platform's">
                        <Box component="thead">
                            <Box component="tr">
                                <Box component="th" sx={{ ...headSx, textAlign: 'left' }}>Your tier ↓ · Platform →</Box>
                                {model.columns.map((c) => (
                                    <Box key={c} component="th" sx={{ ...headSx, textAlign: 'center' }}>
                                        <TierPill code={c} />
                                    </Box>
                                ))}
                                <Box component="th" sx={{ ...headSx, textAlign: 'right' }}>Total</Box>
                            </Box>
                        </Box>
                        <Box component="tbody">
                            {model.rows.map((row) => (
                                <Box key={row} component="tr" sx={{ borderTop: `1px solid ${dsVar('line')}` }}>
                                    <Box component="th" scope="row" sx={{ ...headSx, textAlign: 'left' }}>
                                        {row === NOT_COMPARABLE
                                            ? <Typography component="span" sx={{ fontSize: '0.75rem', color: dsVar('fg-2') }}>Not comparable</Typography>
                                            : <TierPill code={row} />}
                                    </Box>
                                    {model.columns.map((column) => {
                                        const n = cell(row, column);
                                        const diagonal = row === column;
                                        return (
                                            <Box
                                                key={column}
                                                component="td"
                                                data-testid={`cell-${row}-${column}`}
                                                sx={{
                                                    textAlign: 'center', px: 2, py: 1.25, fontSize: '0.9375rem',
                                                    fontVariantNumeric: 'tabular-nums',
                                                    fontWeight: n ? 700 : 400,
                                                    color: n ? (diagonal ? ink('#10B981') : dsVar('fg-body')) : dsVar('fg-faint'),
                                                    outline: diagonal ? '1px solid rgba(16, 185, 129, 0.45)' : 'none',
                                                    outlineOffset: '-3px', borderRadius: '6px'
                                                }}
                                            >
                                                {n ? count(n) : '·'}
                                            </Box>
                                        );
                                    })}
                                    <Box component="td" sx={{ textAlign: 'right', px: 1.5, color: dsVar('fg-2'), fontSize: '0.8125rem' }}>
                                        {count(rowTotal(row))}
                                    </Box>
                                </Box>
                            ))}
                            <Box component="tr" sx={{ borderTop: `1px solid ${dsVar('line')}` }}>
                                <Box component="th" scope="row" sx={{ ...headSx, textAlign: 'left' }}>Total</Box>
                                {model.columns.map((column) => (
                                    <Box key={column} component="td" sx={{ textAlign: 'center', py: 1, color: dsVar('fg-2'), fontSize: '0.8125rem' }}>
                                        {count(columnTotal(column))}
                                    </Box>
                                ))}
                                <Box component="td" sx={{ textAlign: 'right', px: 1.5, color: dsVar('fg-body'), fontSize: '0.8125rem', fontWeight: 700 }}>
                                    {count(model.total)}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            )}
        </SectionCard>
    );
};

export default AgreementMatrix;
